/**
 * Supabase client for the frontend, used for realtime subscriptions.
 *
 * Realtime has to be told who the user is. Every table we subscribe to has RLS, so a socket
 * carrying only the anon key is filtered down to nothing — the subscription connects, reports
 * SUBSCRIBED, and then silently never fires. That is why notifications only appeared on refresh.
 *
 * The token therefore has to be pushed into realtime at startup and again whenever it changes,
 * which is what syncRealtimeAuth does.
 */

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase credentials not found. Realtime features may not work.');
}

const readToken = () => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null;
  } catch {
    return null;
  }
};

/** Mock used when credentials are absent, so the app renders instead of crashing. */
const createMockClient = () => ({
  from: () => ({
    select: () => ({ eq: () => ({ order: () => ({ limit: () => Promise.resolve({ data: [], error: null }) }) }) }),
    insert: () => Promise.resolve({ error: null }),
    update: () => ({ eq: () => Promise.resolve({ error: null }) }),
  }),
  channel: () => ({ on: () => ({ subscribe: () => {} }) }),
  removeChannel: () => {},
  realtime: { setAuth: () => Promise.resolve() },
});

let supabase;
let lastAppliedToken = null;

try {
  if (supabaseUrl && supabaseAnonKey) {
    supabase = createClient(supabaseUrl, supabaseAnonKey, {
      realtime: {
        params: { eventsPerSecond: 10 },
      },
      auth: {
        persistSession: false, // sessions are managed by src/utils/auth.js
        autoRefreshToken: false,
      },
    });
  } else {
    console.warn('Supabase credentials not found. Realtime features disabled.');
    supabase = createMockClient();
  }
} catch (error) {
  console.error('Failed to initialize Supabase client:', error);
  supabase = createMockClient();
}

/**
 * Points realtime at the current auth token.
 *
 * setAuth updates channels that are already open as well as future ones, so this can be called at
 * any time — on login, on token refresh, and on logout (where the token is null and realtime falls
 * back to the anon key).
 *
 * @param {boolean} force Re-apply even if the token looks unchanged, e.g. after a reconnect.
 * @returns {Promise<string|null>} The token that was applied.
 */
export const syncRealtimeAuth = async (force = false) => {
  const token = readToken();
  if (!force && token === lastAppliedToken) return token;

  lastAppliedToken = token;
  try {
    await supabase.realtime.setAuth(token);
  } catch (error) {
    console.warn('Could not apply auth token to realtime:', error?.message || error);
  }
  return token;
};

// Apply whatever token exists before any component gets a chance to subscribe.
const initialAuth = syncRealtimeAuth();

/**
 * Opens a realtime channel once the auth token has been applied.
 *
 * Subscribing before setAuth has settled produces a channel bound to the anon key, which RLS then
 * filters to nothing — the failure is silent, so it is not worth leaving to chance.
 *
 * @param {string} name Channel name
 * @param {Array<{ event?: string, schema?: string, table: string, filter?: string, handler: Function }>} bindings
 * @param {(status: string) => void} [onStatus]
 * @returns {() => void} Unsubscribe, safe to call while the subscription is still opening.
 */
export const subscribeWithAuth = (name, bindings, onStatus) => {
  let channel = null;
  let cancelled = false;

  initialAuth
    .then(() => syncRealtimeAuth())
    .then(() => {
      if (cancelled) return;

      let builder = supabase.channel(name);
      for (const { event = '*', schema = 'public', table, filter, handler } of bindings) {
        builder = builder.on(
          'postgres_changes',
          { event, schema, table, ...(filter ? { filter } : {}) },
          handler
        );
      }

      channel = builder.subscribe((status) => {
        if (onStatus) onStatus(status);
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
          console.warn(`Realtime channel "${name}" reported ${status}`);
        }
      });
    })
    .catch((error) => console.warn(`Could not open realtime channel "${name}":`, error));

  return () => {
    cancelled = true;
    if (channel) supabase.removeChannel(channel);
  };
};

if (typeof window !== 'undefined') {
  // Every login path in the app dispatches this once the token is stored.
  window.addEventListener('user-login', () => syncRealtimeAuth(true));
  window.addEventListener('user-logout', () => syncRealtimeAuth(true));

  // Fires when another tab logs in or out.
  window.addEventListener('storage', (event) => {
    if (event.key === 'auth_token') syncRealtimeAuth(true);
  });

  // Coming back from a background tab is when an expired token tends to surface.
  window.addEventListener('online', () => syncRealtimeAuth(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') syncRealtimeAuth();
  });
}

export { supabase };
